import React, { useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Avatar,
  Paper
} from '@mui/material';
import {
  PersonAdd,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { USER_ROLES } from '../../config/constants';

const UserManagement = () => {
  const { user, role: loggedInUserRole } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    role: 'user'
  });
  const [userTabIndex, setUserTabIndex] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const availableRoles = [
    { value: USER_ROLES.USER, label: 'Gebruiker' },
    { value: USER_ROLES.PROJECT_MANAGER, label: 'Project Manager' },
    ...(loggedInUserRole === USER_ROLES.ADMIN ? [{ value: USER_ROLES.ADMIN, label: 'Administrator' }] : []),
  ];

  const fetchUsersWrapper = useCallback(async (ignoreCancellation = { current: false }) => {
    setLoading(true);
    try {
      if (!loggedInUserRole) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (ignoreCancellation.current) return;
      }
      
      const { data: response, error: functionError } = await supabase.functions.invoke('list-users', {
        method: 'GET'
      });

      if (functionError) throw functionError;
      if (typeof response !== 'object' || response === null || !response.data) {
         throw new Error('Unexpected response structure from list-users function');
      }

      if (response.status === 'error') throw new Error(response.message || 'Error from list-users function');

      const filteredUsers = (response.data || []).filter(u => {
        if (loggedInUserRole === USER_ROLES.ADMIN) {
          return true;
        }
        return u.role !== USER_ROLES.ADMIN;
      });

      if (!ignoreCancellation.current) {
        setUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Error fetching or processing users:', error);
      if (!ignoreCancellation.current) {
         setError(error.message || 'Kon gebruikers niet ophalen.');
      }
    } finally {
      if (!ignoreCancellation.current) {
        setLoading(false);
      }
    }
  }, [loggedInUserRole, setError, setLoading, setUsers]);

  useEffect(() => {
    const ignore = { current: false };
    fetchUsersWrapper(ignore);

    return () => {
      ignore.current = true;
    };
  }, [fetchUsersWrapper]);

  const handleCreateUser = async () => {
    setIsCreating(true);
    setError(null);
    
    if (newUser.role === USER_ROLES.ADMIN && loggedInUserRole !== USER_ROLES.ADMIN) {
        setError('U heeft geen rechten om een Administrator aan te maken.');
        setIsCreating(false);
        return;
    }
    
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Not authenticated or session expired');
      }
      const token = sessionData.session.access_token;
      
      const url = `${supabase.functions.url}/create-user`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newUser.email.trim(),
          role: newUser.role
        })
      });

      let responseData;
      const responseText = await response.text();
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        throw new Error('Invalid response from server: ' + responseText);
      }
      
      if (!response.ok) {
        throw new Error(responseData?.message || responseData?.error || 'Failed to create user');
      }
      
      setSuccessMessage(`Gebruiker ${newUser.email} is succesvol aangemaakt`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      setOpen(false);
      setNewUser({ email: '', role: 'user' });
      
      setTimeout(() => fetchUsersWrapper(), 100); 
      
    } catch (error) {
      console.error('Error creating user:', error);
      setError(typeof error === 'string' ? error : error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
      if (newRole === USER_ROLES.ADMIN && loggedInUserRole !== USER_ROLES.ADMIN) {
          setError('U heeft geen rechten om een gebruiker tot Administrator te promoveren.');
          return;
      }
      
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
          throw new Error('Not authenticated or session expired');
      }
      const token = sessionData.session.access_token;

      const { data, error } = await supabase.functions.invoke('update-user-role', {
        body: { 
          userId,
          newRole
        },
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });

      if (error) throw error;
      if (typeof data !== 'object' || data === null) {
          throw new Error('Invalid response from update-user-role function.');
      }
      if (!data?.success) throw new Error(data?.message || data?.error || 'Rol update mislukt volgens functie response');

      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId ? { ...u, role: newRole } : u
        )
      );
      
      setSuccessMessage(`Rol is succesvol bijgewerkt naar ${newRole}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error('Error in handleUpdateRole:', error);
      setError(`Kon rol niet bijwerken: ${error.message}`);
    }
  };

  const handleToggleUserBan = async (userId, currentlyBanned) => {
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.role === USER_ROLES.ADMIN && loggedInUserRole !== USER_ROLES.ADMIN) {
        setError('Alleen Administrators kunnen andere Administrators blokkeren/deblokkeren.');
        return;
    }
      
    if (userId === user?.id) {
      setError('Je kunt jezelf niet blokkeren/deblokkeren.');
      return;
    }

    const confirmMessage = currentlyBanned
      ? 'Weet je zeker dat je deze gebruiker wilt deblokkeren?'
      : 'Weet je zeker dat je deze gebruiker wilt blokkeren?';

    if (!window.confirm(confirmMessage)) return;

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Not authenticated or session expired');
      }
      const token = sessionData.session.access_token;
      
      const { data, error } = await supabase.functions.invoke('toggle-user-ban', {
        body: { 
          userId,
          ban: !currentlyBanned
        },
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });

      if (error) throw error;
      if (typeof data !== 'object' || data === null) {
        throw new Error('Invalid response from toggle-user-ban function.');
      }
      if (!data?.success) throw new Error(data?.message || data?.error || 'Operatie mislukt volgens functie response');

      setSuccessMessage(currentlyBanned 
        ? 'Gebruiker is gedeblokkeerd'
        : 'Gebruiker is geblokkeerd'
      );
      setTimeout(() => setSuccessMessage(null), 3000);
      
      setTimeout(() => fetchUsersWrapper(), 100); 

    } catch (error) {
      console.error('Ban toggle error:', error);
      setError(error.message);
    }
  };

  const calculateUserStats = (users) => {
    const activeCount = users.filter(u => !u.banned && !u.banned_until).length;
    const bannedCount = users.filter(u => u.banned || u.banned_until).length;
    
    return {
      activeUsers: activeCount,
      bannedUsers: bannedCount,
      totalUsers: users.length
    };
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6"></Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setOpen(true)}
        >
          Nieuwe Gebruiker
        </Button>
      </Box>

      <Tabs
        value={userTabIndex}
        onChange={(e, newValue) => setUserTabIndex(newValue)}
        sx={{ mb: 2 }}
        variant="standard"
      >
        <Tab 
          label={`Actieve Gebruikers (${calculateUserStats(users).activeUsers})`}
          sx={{ fontWeight: 'medium' }}
        />
        <Tab 
          label={`Geblokkeerde Gebruikers (${calculateUserStats(users).bannedUsers})`}
          sx={{ fontWeight: 'medium' }}
        />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Gebruiker</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell align="right">Acties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography color="textSecondary" display="inline">
                    Gebruikers laden...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users
                .filter(u => {
                  return userTabIndex === 0 
                    ? !u.banned && !u.banned_until
                    : u.banned || u.banned_until;
                })
                .map((listedUser) => (
                  <TableRow 
                    key={listedUser.id}
                    sx={{ 
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>
                          {listedUser.email[0].toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {listedUser.email.split('@')[0]}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{listedUser.email}</TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth disabled={listedUser.id === user?.id && loggedInUserRole === USER_ROLES.ADMIN}>
                        <Select
                          id={`user-role-select-${listedUser.id}`}
                          value={listedUser.role || USER_ROLES.USER}
                          onChange={(e) => {
                            handleUpdateRole(listedUser.id, e.target.value);
                          }}
                          disabled={(listedUser.id === user?.id && loggedInUserRole === USER_ROLES.ADMIN) || (listedUser.role === USER_ROLES.ADMIN && loggedInUserRole !== USER_ROLES.ADMIN)}
                        >
                          {availableRoles.map((roleOption) => (
                            <MenuItem 
                              key={roleOption.value} 
                              value={roleOption.value}
                              disabled={roleOption.value === USER_ROLES.ADMIN && listedUser.role === USER_ROLES.ADMIN && listedUser.id !== user?.id && loggedInUserRole !== USER_ROLES.ADMIN}
                              >
                              {roleOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        color={userTabIndex === 1 ? "success" : "error"}
                        startIcon={userTabIndex === 1 ? <CheckCircleIcon /> : <BlockIcon />}
                        onClick={() => {
                          handleToggleUserBan(listedUser.id, userTabIndex === 1);
                        }}
                        disabled={(listedUser.role === USER_ROLES.ADMIN && loggedInUserRole !== USER_ROLES.ADMIN) || listedUser.id === user?.id}
                      >
                        {userTabIndex === 1 ? 'Deblokkeren' : 'Blokkeren'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            )}
            {!loading && users.filter(user => 
              userTabIndex === 0 ? !user.banned && !user.banned_until : user.banned || user.banned_until
            ).length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    {userTabIndex === 0 
                      ? 'Geen actieve gebruikers gevonden' 
                      : 'Geen geblokkeerde gebruikers gevonden'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Nieuwe Gebruiker Uitnodigen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Rol</InputLabel>
            <Select
              id="new-user-role-select"
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              label="Rol"
            >
              {availableRoles.map((roleOption) => (
                <MenuItem key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isCreating}>
            Annuleren
          </Button>
          <Button 
            onClick={handleCreateUser} 
            variant="contained"
            disabled={isCreating || !newUser.email || !newUser.role}
          >
            {isCreating ? 'Bezig met uitnodigen...' : 'Uitnodigen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserManagement;
